/**
 * OrderLineCard — one line in the 新規発注 dialog.
 *
 * Design goals (per user "資材 have to manual input everything and the data
 * is not connected across the apps, like what i meant in the 品目 meaning
 * and fields is all over the place"):
 *
 * 1. **Search-first**: top of the card is a big 品目検索 that matches SKU /
 *    name / drawing_no / maker case-insensitively against `mfg_items`. On
 *    pick, every canonical field snaps in from the master.
 * 2. **Master-link visible**: when an item is linked, show a green
 *    「品目マスターリンク済 · <sku>」 chip so buyers KNOW the data will flow
 *    through to receipt / invoice / stock (no more "why isn't maker
 *    autofilling" confusion).
 * 3. **Rare fields hidden by default**: 製番, ユニットNo., 改訂, 材質 are
 *    collapsed into a 「詳細」 section — always available, never in the way.
 * 4. **Field order matches PartsRequest**: same layout everywhere so buyers
 *    learn one mental model.
 * 5. **履歴から提案 stays**: rich past-PO suggestions with vendor + price
 *    hydrate every blank field on click (backend already rich).
 */
import { finalizePendingSku } from '@/lib/skuPending';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MagnifyingGlass, X, Sparkle, CaretDown, Link as LinkIcon, ShoppingCart, Image as ImageIcon, Warning, Star, Lightning, CopySimple, PencilSimple, Plus, Hash, Bookmark, LockSimple, ArrowsClockwise } from '@phosphor-icons/react';
import { shizaiApi } from './shizaiApi';
import LookupCombobox from '../../components/ui/lookup-combobox';
import { toast } from 'sonner';
import { SkuWizard } from '@/components/SkuWizard';
import { skuMajorTitle, groupTemplatesByName } from '@/lib/skuDisplay';
import { filterLedgerLocked } from '@/lib/ledgerPrefixes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Segment kinds that resolve automatically at issue time (no user input).
// Kept in sync with routers/sku.py::_build_sku.
const AUTO_KINDS = new Set(['literal', 'year2', 'year4', 'month', 'counter']);

const yen = (n) => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 }).format(Number(n) || 0);

/** Clipboard write with a textarea/execCommand fallback (Safari / permission-denied contexts). */
async function copyText(s) {
  try {
    await navigator.clipboard.writeText(s);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = s;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * Normalise a user-typed URL so `new URL()` parses. Requesters often paste
 * URLs without the scheme ("amazon.co.jp/..."), which would otherwise throw
 * and cause the whole clickable-link row to silently disappear.
 */
function normaliseUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * One click-to-copy field chip inside the green master-link block. Clicking
 * copies ONLY this field's value (per user "when I click 型式 it copies the
 * value so I can paste it") — no bundled multi-field copy anymore.
 */
function CopyChip({ label, value, mono, testId }) {
  const s = value === null || value === undefined ? '' : String(value).trim();
  if (!s) return null;
  const copy = async () => {
    if (await copyText(s)) toast.success(`${label}をコピーしました`);
    else toast.error('コピーに失敗しました');
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      title={`クリックで「${s}」をコピー`}
      className="group inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2 py-1 text-left hover:bg-emerald-100 hover:border-emerald-500 active:scale-[0.98] transition-colors max-w-full"
    >
      <span className="text-[9px] font-bold text-emerald-600 shrink-0 uppercase tracking-wide">{label}</span>
      <span className={`text-xs text-zinc-800 truncate ${mono ? 'font-mono' : ''}`}>{s}</span>
      <CopySimple size={12} weight="bold" className="text-emerald-300 group-hover:text-emerald-700 shrink-0" />
    </button>
  );
}

/**
 * @param {object}   line          The line state object (see emptyLine in OrderFormDialog)
 * @param {number}   index         Index in the parent lines[] array
 * @param {object[]} items         All mfg_items (fetched once by parent)
 * @param {function} setLine       (index, patch) => update parent state for a subset of fields
 * @param {function} onRemove      Called when user clicks the X — hide when only 1 line
 * @param {boolean}  canRemove
 * @param {function} onPickHistory (index, suggestion) => rehydrate line + optionally supplier
 * @param {boolean}  disableEdit   True during a network save
 */
export default function OrderLineCard({ line, index, items, setLine, onRemove, canRemove,
                                        onPickHistory, disableEdit,
                                        supplierCandidates, onPickSupplier,
                                        suppliers, onChangeSupplier, taxDivisor = 1 }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // null=untouched, []=fetched no history
  const [loadingSug, setLoadingSug] = useState(false);
  const searchInputRef = useRef(null);
  // iter_138 · SKU auto-issue integration (mirrors Seizo's ItemFormDialog UX):
  //   • [Hash] button opens SkuWizard with defaultApp="shizai"
  //   • Template dropdown lets buyer issue with a one-tap for shizai-scoped rules
  //   • On success the resulting SKU is written to line.part_no
  const [skuTemplates, setSkuTemplates] = useState([]);
  const [skuWizardOpen, setSkuWizardOpen] = useState(false);
  const [tplPickBusy, setTplPickBusy] = useState(false);

  // Fetch templates once per mount, scoped to 資材 (shizai).
  // 20260610 · The 20260721 "templates are PUBLIC" rule is reversed — EQP
  // (設備台帳) rules were leaking into pickers that must not issue them. Rules
  // with a blank app_code still match (compatibility path), and equipment-ledger
  // prefixes are dropped outright.
  useEffect(() => {
    axios.get(`${API}/sku/templates`, { params: { app_code: 'shizai' }, withCredentials: true })
      .then((r) => filterLedgerLocked(r.data || []))
      .then(setSkuTemplates)
      .catch(() => setSkuTemplates([]));
  }, []);

  const templatesByName = useMemo(() => groupTemplatesByName(skuTemplates), [skuTemplates]);

  const handleSkuIssued = async (sku, meta) => {
    // Per user request 20260721: "Just fill 品番 in the current line" — no
    // auto-fill of 品名 or master-registration side effects.
    // 20260805: wizard はプレビューを返すため、行に確定転記する時点で正式発番する。
    let finalSku = sku;
    if (meta?.pending) {
      try {
        finalSku = await finalizePendingSku(meta.pending);
      } catch (e) {
        toast.error(e?.response?.data?.detail || '品番の発番に失敗しました');
        return;
      }
    }
    setLine(index, { part_no: finalSku });
    toast.success(`品番 ${finalSku} を発番しました`);
  };

  // Template quick-pick: if all non-auto segments are pre-filled, issue in one
  // tap. Otherwise open the wizard with the template pre-applied.
  const handleTemplateQuickPick = async (code) => {
    if (!code || code === '__none__') return;
    const tpl = skuTemplates.find((x) => x.code === code);
    if (!tpl) return;
    const missing = (tpl.segments || [])
      .filter((s) => !AUTO_KINDS.has(s.kind))
      .filter((s) => !(tpl.values || {})[s.key]);
    if (missing.length === 0) {
      setTplPickBusy(true);
      try {
        const r = await axios.post(
          `${API}/sku/issue`,
          { template_id: tpl.id, values: tpl.values || {} },
          { withCredentials: true },
        );
        handleSkuIssued(r.data.sku);
      } catch (e) {
        toast.error(e?.response?.data?.detail || '発番に失敗しました');
      } finally {
        setTplPickBusy(false);
      }
    } else {
      // Incomplete template — fall back to the wizard so the buyer can fill in
      // the required segments (e.g. `unit_code`, `category`).
      toast.info(`${missing.length}項目の入力が必要です`);
      setSkuWizardOpen(true);
    }
  };

  // Local case-insensitive item search across the 4 most useful fields.
  // Not a debounced network call — `items` is already fetched once by the
  // parent, so this is instant and works offline (unlike shizaiApi.suggest).
  const q = searchQuery.trim().toLowerCase();
  const results = q ? items.filter((it) => {
    const hay = `${it.name || ''} ${it.sku || ''} ${it.drawing_no || ''} ${it.maker || ''} ${it.maker_pn || ''}`.toLowerCase();
    return hay.includes(q);
  }).slice(0, 12) : [];

  // Blank-safe pick: fill every canonical field the buyer hasn't typed yet.
  // Also links `item_id` so downstream (receipt/invoice/stock) matches by id.
  const pickItem = (it) => {
    setLine(index, {
      item_id: it.id,
      part_no: line.part_no || it.sku || '',
      name: line.name || it.name || '',
      drawing_no: line.drawing_no || it.drawing_no || '',
      maker: line.maker || it.maker || '',
      maker_pn: line.maker_pn || it.maker_pn || '',
      model_no: line.model_no || it.model_no || '',
      material: line.material || it.material || '',
      uom: line.uom || it.uom || '個',
      unit_price: (Number(line.unit_price) || 0) ? line.unit_price : (it.cost || 0),
    });
    setSearchQuery('');
    setSearchOpen(false);
    toast.success(`${it.name} をリンクしました`);
  };

  const clearLink = () => setLine(index, { item_id: '' });


  // Fetch vendor history when the buyer clicks 履歴から提案. Blank-safe apply
  // is delegated to the parent via `onPickHistory` (which also updates the
  // top-level supplier picker).
  const fetchHistory = useCallback(async () => {
    const pn = (line.part_no || '').trim();
    if (!pn) { toast.error('品番を入力してください'); return; }
    setLoadingSug(true);
    try {
      const r = await shizaiApi.suggest(pn);
      setSuggestions(r.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally { setLoadingSug(false); }
  }, [line.part_no]);

  // Close the search dropdown when clicking outside
  useEffect(() => {
    if (!searchOpen) return;
    const onDocClick = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [searchOpen]);

  // 税込み入力時は税抜換算した単価で金額を出す（フォーム側の小計と一致させる）
  const effPrice = Math.round((Number(line.unit_price) || 0) / (taxDivisor || 1));
  const amount = Math.round((Number(line.qty) || 0) * effPrice);
  const taxConverted = (taxDivisor || 1) !== 1 && Number(line.unit_price) > 0;
  const linkedItem = line.item_id ? items.find((x) => x.id === line.item_id) : null;
  const ps = Number(linkedItem?.pack_size) || 0;
  const pkQty = ps > 0 && Number(line.qty) > 0
    && Math.abs(Number(line.qty) / ps - Math.round(Number(line.qty) / ps)) < 1e-9
    ? Math.round(Number(line.qty) / ps) : null;
  // Purchase URL: prefer the requester-supplied reference_url, fall back to the
  // 品目マスター's registered 購入URL (mfg_url) so master-linked lines ALWAYS show
  // the clickable vendor button when a URL is registered on the item.
  const effectiveUrl = line.reference_url || linkedItem?.mfg_url || '';
  const purchaseHref = normaliseUrl(effectiveUrl);
  const hasPurchaseData = !!effectiveUrl || !!line.photo_url;

  // Aggressive minimalism per user "shows only what information shizai need
  // to order and input": when the line is linked to 品目マスター (identity is
  // certain), collapse メーカー/型式/図番/材質 into 詳細 — buyer only needs to
  // ORDER (品名/品番visible via chip) and INPUT (数量/単価). All the
  // denormalised master data still saves with the PO but doesn't clutter.
  // When NOT linked, show fields inline so buyer can create the master data.
  const [minimal, setMinimal] = useState(!!linkedItem);
  useEffect(() => { setMinimal(!!linkedItem); }, [linkedItem, line.item_id]);
  const isIncomplete = !linkedItem && !(line.maker && line.drawing_no && line.uom);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3 relative" data-testid={`order-line-${index}`}>
      {canRemove && (
        <button
          onClick={onRemove}
          data-testid={`order-line-remove-${index}`}
          className="absolute top-2 right-2 text-zinc-400 hover:text-rose-600 disabled:opacity-40"
          disabled={disableEdit}
        >
          <X size={16} />
        </button>
      )}

      {/* ── ROW 0 (requester context): purchase link / photo / tax-inclusive
             chip — appears ONLY when the source request has this data. This
             is the "click the link to buy" affordance so the buyer can
             actually open Amazon / MonotaRO / etc. from a parts-request line
             without hunting for it. ─────────────────────────────────────── */}
      {(hasPurchaseData || line.was_tax_inclusive || line.request_line_notes) && (
        <div className="mb-2 space-y-1" data-testid={`order-line-request-context-${index}`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            {purchaseHref && (
              <a
                href={purchaseHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100"
                data-testid={`order-line-purchase-link-${index}`}
                title={`購入ページを開く · ${effectiveUrl}`}
              >
                <ShoppingCart size={12} weight="bold" />
                URLを開く
              </a>
            )}
            {line.photo_url && (
              <a
                href={line.photo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-zinc-300 bg-zinc-50 text-zinc-700 hover:bg-zinc-100 text-[10px] font-semibold"
                data-testid={`order-line-photo-link-${index}`}
                title="依頼者が添付した参考画像/資料を開く"
              >
                <ImageIcon size={11} weight="bold" />
                参考資料
              </a>
            )}
            {line.was_tax_inclusive && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-orange-300 bg-orange-50 text-orange-800 text-[10px] font-semibold"
                data-testid={`order-line-tax-inclusive-${index}`}
                title="依頼元は税込み価格で登録されていたため、税抜きに自動換算しました（÷1.10）"
              >
                税込み価格から自動換算済
              </span>
            )}
            {isIncomplete && effectiveUrl && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[10px]"
                data-testid={`order-line-incomplete-${index}`}
                title="メーカー / 図番 / 単位 のいずれかが未入力です — 上の購入ページを開いて補完してください"
              >
                <Warning size={11} weight="fill" />
                未入力あり — 購入ページで確認
              </span>
            )}
          </div>
          {/* Show the raw URL beneath the button so the buyer can copy/verify it
              (some requesters paste multi-page links and the buyer wants to
              confirm the exact target before ordering). Truncated with a
              native title tooltip for the full URL. */}
          {effectiveUrl && (
            <div className="flex items-center gap-1 text-[10px] text-zinc-400 font-mono truncate"
                 data-testid={`order-line-purchase-url-text-${index}`}
                 title={effectiveUrl}>
              <LinkIcon size={10} weight="bold" className="shrink-0" />
              <span className="truncate">{effectiveUrl}</span>
              {!line.reference_url && linkedItem?.mfg_url && (
                <span className="shrink-0 text-emerald-600/80 font-sans">(品目マスター登録URL)</span>
              )}
              <button
                type="button"
                onClick={async () => {
                  if (await copyText(effectiveUrl)) toast.success('URLをコピーしました');
                  else toast.error('コピーに失敗しました');
                }}
                className="ml-1 shrink-0 text-zinc-500 hover:text-zinc-800 underline"
                data-testid={`order-line-purchase-url-copy-${index}`}
              >
                コピー
              </button>
            </div>
          )}
          {/* Requester's line-level notes — the "why / what" behind the ask.
              Buyer often needs this to phrase the supplier request correctly
              (e.g. "急ぎで納期優先" or "1個だけ試作用"). */}
          {line.request_line_notes && (
            <div
              className="inline-flex items-start gap-1 px-2 py-1 rounded-md border border-sky-200 bg-sky-50/70 text-[11px] text-sky-900 max-w-full"
              data-testid={`order-line-request-notes-${index}`}
              title="依頼者の備考"
            >
              <span className="text-[9px] font-semibold text-sky-700 shrink-0 mt-0.5">依頼備考:</span>
              <span className="whitespace-pre-wrap break-words">{line.request_line_notes}</span>
            </div>
          )}
        </div>
      )}

      {/* ── ROW 0.4: 仕入先の付け替え (Jul 2026) — request-sourced lines get a
             compact per-line supplier picker so the buyer can MOVE a line to a
             different 仕入先 (or clear it) right here. Persists to the REQ. ── */}
      {Array.isArray(suppliers) && onChangeSupplier && (
        <div className="mb-2 flex items-center gap-1.5 flex-wrap" data-testid={`order-line-supplier-row-${index}`}>
          <span className="text-[10px] font-semibold text-zinc-500 shrink-0">仕入先:</span>
          <select
            value={line.supplier_id || ''}
            onChange={(e) => onChangeSupplier(index, e.target.value || null)}
            disabled={disableEdit}
            data-testid={`order-line-supplier-select-${index}`}
            className={`h-7 max-w-[240px] rounded-md border px-2 text-[11px] font-medium outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-40 ${
              line.supplier_id ? 'border-zinc-300 bg-white text-zinc-800' : 'border-amber-300 bg-amber-50 text-amber-800'
            }`}
            title="この明細の仕入先を変更します（依頼にも反映されます）"
          >
            <option value="">未指定</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── ROW 0.5: supplier suggestion chips (Feb 2026) — appears only when
             this line came from a REQ that lacks a supplier_id AND the backend
             returned candidates for it. Click applies to line.supplier_id in
             the parent's local state (no persist until PO save). ─────────── */}
      {!line.supplier_id && (supplierCandidates || []).length > 0 && (
        <div
          className="mb-2 flex items-center gap-1.5 flex-wrap"
          data-testid={`order-line-sup-suggest-${index}`}
        >
          <span className="text-[10px] font-semibold text-zinc-500 shrink-0">仕入先候補:</span>
          {supplierCandidates.map((c) => {
            const cls = c.reason === 'preferred'
              ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
              : c.reason === 'last_po'
              ? 'bg-sky-50 text-sky-800 border-sky-300 hover:bg-sky-100'
              : 'bg-violet-50 text-violet-800 border-violet-300 hover:bg-violet-100';
            const Icon = c.reason === 'preferred' ? Star : Lightning;
            return (
              <button
                key={c.supplier_id}
                type="button"
                onClick={() => onPickSupplier && onPickSupplier(index, c.supplier_id, c.supplier_name)}
                disabled={disableEdit}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors disabled:opacity-40 ${cls}`}
                data-testid={`order-line-sup-suggest-chip-${index}-${c.reason}`}
                title={c.detail}
              >
                <Icon size={10} weight="fill" />
                <span className="font-semibold"><span data-no-translate>{c.supplier_name}</span></span>
                <span className="opacity-70 text-[9px]">{c.detail}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── ROW 1: item search / master link block ────────────────────
             Standout redesign (per user "make the green field more standout
             and easy to understand"): stronger border + header row, then a
             grid of click-to-copy field chips. Clicking any chip copies
             ONLY that field's value — the buyer pastes it straight into a
             supplier order form / Outlook, one field at a time. ────────── */}
      {linkedItem ? (
        <div className="mb-3 rounded-lg bg-emerald-50 border-2 border-emerald-300 overflow-hidden"
             data-testid={`order-line-master-link-${index}`}>
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-100/70 border-b border-emerald-200">
            <LinkIcon size={15} weight="fill" className="text-emerald-700 shrink-0" />
            <span className="text-xs font-bold text-emerald-900 shrink-0">品目マスターリンク済</span>
            <span className="font-mono text-xs font-semibold text-emerald-700 shrink-0">{linkedItem.sku}</span>
            <span className="text-xs text-emerald-900 truncate flex-1 font-semibold" title={linkedItem.name}>{linkedItem.name}</span>
            <button
              onClick={clearLink}
              className="text-[10px] text-emerald-700 hover:text-emerald-900 underline shrink-0"
              data-testid={`order-line-unlink-${index}`}
              disabled={disableEdit}
            >
              リンク解除
            </button>
          </div>
          {minimal && (
            <div className="px-3 py-2" data-testid={`order-line-master-summary-${index}`}>
              <div className="flex items-center gap-1 mb-1.5 text-[10px] text-emerald-700 font-semibold">
                <CopySimple size={11} weight="bold" />
                フィールドをクリックすると値をコピーできます
              </div>
              <div className="flex flex-wrap gap-1.5">
                <CopyChip label="品名" value={line.name} testId={`order-line-copy-name-${index}`} />
                <CopyChip label="品番" value={line.part_no} mono testId={`order-line-copy-partno-${index}`} />
                <CopyChip label="メーカー" value={line.maker} testId={`order-line-copy-maker-${index}`} />
                <CopyChip label="メーカー品番" value={line.maker_pn} mono testId={`order-line-copy-makerpn-${index}`} />
                <CopyChip label="型式" value={line.model_no} testId={`order-line-copy-modelno-${index}`} />
                <CopyChip label="図番" value={line.drawing_no} mono testId={`order-line-copy-drawingno-${index}`} />
                <CopyChip label="材質" value={line.material} testId={`order-line-copy-material-${index}`} />
                <CopyChip label="製番" value={line.seibang} mono testId={`order-line-copy-seibang-${index}`} />
                {Number(line.qty) > 0 && (
                  <CopyChip label="数量" value={`${Number(line.qty)} ${line.uom || ''}`.trim()} testId={`order-line-copy-qty-${index}`} />
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative mb-3" ref={searchInputRef}>
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            placeholder="品目マスターから検索（品名 / 品番 / 図番 / メーカー）..."
            className="h-9 pl-8 text-xs bg-zinc-50 border-dashed"
            data-testid={`order-line-search-${index}`}
            disabled={disableEdit}
          />
          {searchOpen && q && results.length > 0 && (
            <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
                 data-testid={`order-line-search-results-${index}`}>
              {results.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => pickItem(it)}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-zinc-200 last:border-b-0"
                  data-testid={`order-line-search-result-${index}-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-zinc-800 truncate">{it.name}</div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-x-2 gap-y-0.5 flex-wrap mt-0.5">
                        {it.sku && <span className="font-mono text-zinc-600">{it.sku}</span>}
                        {it.maker_pn && <span className="font-mono text-zinc-500">{it.maker_pn}</span>}
                        {it.model_no && <span className="text-zinc-600">型式 {it.model_no}</span>}
                        {it.drawing_no && <span className="text-zinc-400">図 {it.drawing_no}</span>}
                        {it.maker && <span className="text-zinc-400">· {it.maker}</span>}
                      </div>
                    </div>
                    {it.on_hand !== undefined && (
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${it.on_hand > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                        在庫 {it.on_hand}{it.uom || '個'}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {searchOpen && q && results.length === 0 && (
            <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-md shadow-lg px-3 py-2 text-[11px] text-zinc-400">
              マスターに一致する品目がありません — 下で新規入力
            </div>
          )}
        </div>
      )}

      {/* ── ROW 2: 品名 + 品番 + 図番 ───────────────────────────────── */}
      {(!minimal || !linkedItem) && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="md:col-span-2">
          <Label className="text-[10px] text-zinc-500">品名 / 形式寸法</Label>
          <Input
            data-testid={`order-line-name-${index}`}
            value={line.name || ''}
            onChange={(e) => setLine(index, { name: e.target.value })}
            className="h-9 mt-0.5 text-xs"
            disabled={disableEdit}
          />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">品番</Label>
          <div className="flex items-center gap-1 mt-0.5">
            <Input
              data-testid={`order-line-partno-${index}`}
              value={line.part_no || ''}
              onChange={(e) => setLine(index, { part_no: e.target.value })}
              className="h-9 font-mono text-xs flex-1"
              disabled={disableEdit}
            />
            <Button
              type="button"
              variant="outline"
              className="h-9 w-9 px-0 shrink-0"
              onClick={() => setSkuWizardOpen(true)}
              disabled={disableEdit}
              data-testid={`order-line-sku-generator-btn-${index}`}
              title="品番自動採番（資材ルール）"
            >
              <Hash size={13} />
            </Button>
          </div>
          {skuTemplates.length > 0 && (
            <div className="mt-1 flex items-center gap-1" data-testid={`order-line-sku-tpl-row-${index}`}>
              <Bookmark weight="duotone" size={11} className="text-amber-500 shrink-0" />
              <Select value="" onValueChange={handleTemplateQuickPick} disabled={disableEdit || tplPickBusy}>
                <SelectTrigger
                  className="h-7 flex-1 text-[10px] bg-amber-50/40 border-amber-200"
                  data-testid={`order-line-sku-tpl-picker-${index}`}
                >
                  <SelectValue placeholder={tplPickBusy ? '発番中…' : 'ルールから発番 (ワンタップ)'}>
                    {tplPickBusy && (
                      <span className="flex items-center">
                        <ArrowsClockwise className="animate-spin mr-1" size={10} />
                        発番中…
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {templatesByName.map((g) => (
                    <SelectGroup key={g.name || '__flat__'}>
                      {g.name && (
                        <SelectLabel className="text-[10px] text-zinc-400">{g.name}</SelectLabel>
                      )}
                      {g.items.map((tpl) => {
                        const missing = (tpl.segments || [])
                          .filter((s) => !AUTO_KINDS.has(s.kind))
                          .filter((s) => !(tpl.values || {})[s.key]).length;
                        const oneTap = missing === 0;
                        return (
                          <SelectItem key={tpl.code} value={tpl.code} data-testid={`order-line-sku-tpl-opt-${index}-${tpl.code}`}>
                            <span className="flex items-center gap-1">
                              {oneTap ? (
                                <Sparkle weight="fill" size={10} className="text-emerald-500" />
                              ) : (
                                <LockSimple weight="fill" size={10} className="text-zinc-400" />
                              )}
                              <span className="font-semibold text-xs">{skuMajorTitle(tpl)}</span>
                              <span className="text-[10px] text-zinc-500">{tpl.code}</span>
                              {!oneTap && <span className="text-[9px] text-zinc-400">({missing}項目要入力)</span>}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">図番</Label>
          <Input
            data-testid={`order-line-drawingno-${index}`}
            value={line.drawing_no || ''}
            onChange={(e) => setLine(index, { drawing_no: e.target.value })}
            className="h-9 mt-0.5 font-mono text-xs"
            disabled={disableEdit}
          />
        </div>
      </div>
      )}

      {/* ── ROW 3: メーカー + メーカー品番 + 型式 ───────────────────── */}
      {(!minimal || !linkedItem) && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
        <div>
          <Label className="text-[10px] text-zinc-500">メーカー</Label>
          <LookupCombobox
            kind="makers"
            value={line.maker || ''}
            onChange={(v) => setLine(index, { maker: v })}
            testId={`order-line-maker-${index}`}
            inputClassName="mt-0.5"
            disabled={disableEdit}
          />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">メーカー品番</Label>
          <Input
            data-testid={`order-line-makerpn-${index}`}
            value={line.maker_pn || ''}
            onChange={(e) => setLine(index, { maker_pn: e.target.value })}
            className="h-9 mt-0.5 font-mono text-xs"
            disabled={disableEdit}
          />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">型式・仕様</Label>
          <Input
            data-testid={`order-line-modelno-${index}`}
            value={line.model_no || ''}
            onChange={(e) => setLine(index, { model_no: e.target.value })}
            className="h-9 mt-0.5 text-xs"
            disabled={disableEdit}
          />
        </div>
      </div>
      )}

      {/* ── ROW 4: qty + uom + unit_price + amount ────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 items-end">
        <div>
          <Label className="text-[10px] text-zinc-500">数量</Label>
          <Input
            type="number"
            data-testid={`order-line-qty-${index}`}
            value={line.qty}
            onChange={(e) => setLine(index, { qty: e.target.value })}
            className="h-9 mt-0.5 text-xs tabular-nums font-semibold"
            disabled={disableEdit}
          />
          {ps > 0 && (
            <p className="text-[10px] text-zinc-400 mt-0.5 whitespace-nowrap" data-testid={`order-line-pack-${index}`}>
              {pkQty ? `= ${pkQty}${linkedItem?.pack_uom || '袋'} ` : ''}(1{linkedItem?.pack_uom || '袋'}={ps}{linkedItem?.uom || '個'})
            </p>
          )}
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">単位</Label>
          <LookupCombobox
            kind="uoms"
            value={line.uom || ''}
            onChange={(v) => setLine(index, { uom: v })}
            testId={`order-line-uom-${index}`}
            inputClassName="mt-0.5"
            disabled={disableEdit}
          />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">単価{taxConverted && <span className="ml-1 text-orange-600">(税込入力)</span>}</Label>
          <Input
            type="number"
            data-testid={`order-line-price-${index}`}
            value={line.unit_price}
            onChange={(e) => setLine(index, { unit_price: e.target.value })}
            className="h-9 mt-0.5 text-xs tabular-nums"
            disabled={disableEdit}
          />
          {taxConverted && (
            <p className="text-[10px] text-orange-600 mt-0.5" data-testid={`order-line-tax-converted-${index}`}>税抜 {yen(effPrice)}</p>
          )}
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">金額{taxConverted && <span className="ml-1 text-orange-600">(税抜)</span>}</Label>
          <div className="h-9 mt-0.5 flex items-center px-2 text-xs font-semibold text-zinc-800 tabular-nums bg-zinc-50 rounded border border-zinc-200">
            {yen(amount)}
          </div>
        </div>
      </div>

      {/* ── ROW 5: unified action bar ────────────────────────────────
         全項目を編集 / 詳細項目 / 履歴から提案. Copy is now per-field via the
         chips in the green master block above (no bundled copy button). */}
      <div className="mt-3 pt-2 border-t border-zinc-200 flex items-center gap-1.5 flex-wrap"
           data-testid={`order-line-actions-${index}`}>
        {linkedItem && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setMinimal((v) => !v)}
            className="h-8 text-xs"
            data-testid={`order-line-minimal-toggle-${index}`}
          >
            {minimal ? <PencilSimple size={12} className="mr-1" /> : <CaretDown size={12} className="mr-1" weight="bold" />}
            {minimal ? '全項目を編集' : '編集を折りたたむ'}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setShowDetails((v) => !v)}
          className="h-8 text-xs"
          data-testid={`order-line-details-toggle-${index}`}
          title="製番・ユニットNo.・改訂・材質"
        >
          {showDetails ? <CaretDown size={12} className="mr-1" weight="bold" /> : <Plus size={12} className="mr-1" weight="bold" />}
          詳細項目
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={fetchHistory}
          disabled={loadingSug || disableEdit}
          className="h-8 text-xs border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300"
          data-testid={`order-line-suggest-${index}`}
        >
          <Sparkle size={12} className="mr-1" weight="fill" />
          {loadingSug ? '検索中...' : '履歴から提案'}
        </Button>
      </div>
      {showDetails && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid={`order-line-details-${index}`}>
          <div>
            <Label className="text-[10px] text-zinc-500">製番</Label>
            <Input
              value={line.seibang || ''}
              onChange={(e) => setLine(index, { seibang: e.target.value })}
              className="h-9 mt-0.5 font-mono text-xs"
              disabled={disableEdit}
            />
          </div>
          <div>
            <Label className="text-[10px] text-zinc-500">ユニットNo.</Label>
            <Input
              value={line.unit_no || ''}
              onChange={(e) => setLine(index, { unit_no: e.target.value })}
              className="h-9 mt-0.5 font-mono text-xs"
              disabled={disableEdit}
            />
          </div>
          <div>
            <Label className="text-[10px] text-zinc-500">改訂</Label>
            <Input
              value={line.revision || ''}
              onChange={(e) => setLine(index, { revision: e.target.value })}
              className="h-9 mt-0.5 font-mono text-xs"
              disabled={disableEdit}
            />
          </div>
          <div>
            <Label className="text-[10px] text-zinc-500">材質</Label>
            <LookupCombobox
              kind="materials"
              value={line.material || ''}
              onChange={(v) => setLine(index, { material: v })}
              testId={`order-line-material-${index}`}
              inputClassName="mt-0.5"
              disabled={disableEdit}
            />
          </div>
        </div>
      )}

      {/* ── ROW 6: 履歴から提案 results ─────────────────────────── */}
      {suggestions !== null && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`order-line-suggestions-${index}`}>
          {suggestions.length === 0 ? (
            <span className="text-[11px] text-zinc-400">過去の発注実績がありません</span>
          ) : (
            suggestions.map((s, k) => (
              <button
                key={`${s.supplier_id || s.supplier_name || 'sugg'}-${s.unit_price ?? ''}-${k}`}
                type="button"
                onClick={() => onPickHistory(index, s)}
                data-testid={`order-line-sugg-${index}-${k}`}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-rose-200 bg-white text-[11px] text-zinc-700 hover:bg-rose-50"
              >
                <span className="font-medium">{s.supplier_name || '—'}</span>
                <span className="text-rose-600 font-semibold">{yen(s.unit_price)}</span>
                {s.lead_time_days != null && <span className="text-zinc-400">納期{s.lead_time_days}日</span>}
              </button>
            ))
          )}
        </div>
      )}
      {/* iter_138 · Controlled SKU wizard for the 品番 field */}
      <SkuWizard
        open={skuWizardOpen}
        onOpenChange={setSkuWizardOpen}
        defaultApp="shizai"
        onIssued={handleSkuIssued}
      />
    </div>
  );
}
